import type { RuntimeKind } from '../types';
import { RUNTIME_CAPABILITIES } from '../capabilities';
import type { PlatformStorage, PlatformSecureWritePolicy } from './types';
import { createElectronSecureStorage } from '../electron/secure-storage.electron';

/**
 * PlatformStorage implementations (PLAN §5.1).
 *
 * `keychain` and `local-storage` delegate to the existing
 * `secureStorageService` singleton — its namespace migration, localStorage
 * migration fallbacks and `nativeReadFailed` diagnostics are today's behaviour
 * and stay exactly as they are. The import is dynamic so the secure-storage
 * plugin never enters this module's (boot-critical) static graph.
 *
 * `electron-safe-storage` is the real main-process `safeStorage` bridge (T3.C).
 * It is never the plugin's plaintext-localStorage web impl, which would report
 * `source: 'native'` for the Auth-V3 device session (hazard F2), and it fails
 * closed rather than degrading when the OS cannot encrypt (Q4 / A3.4).
 */

async function secureStorage() {
  const module = await import('src/utils/Services/secure-storage.service');
  return module.secureStorageService;
}

function createDelegatingStorage(kind: 'keychain' | 'local-storage'): PlatformStorage {
  return {
    kind,
    async initialize(): Promise<void> {
      await (await secureStorage()).initialize();
    },
    async get(key: string): Promise<string | null> {
      return (await secureStorage()).get(key);
    },
    async set(key: string, value: string, policy?: PlatformSecureWritePolicy): Promise<void> {
      await (await secureStorage()).set(key, value, policy ? { policy } : undefined);
    },
    async remove(key: string): Promise<void> {
      await (await secureStorage()).remove(key);
    },
    async keys(): Promise<string[]> {
      return (await secureStorage()).getAllKeys();
    },
    async clear(): Promise<void> {
      const service = await secureStorage();
      const keys = await service.getAllKeys();
      await keys.reduce<Promise<void>>(async (previous, key) => {
        await previous;
        await service.remove(key);
      }, Promise.resolve());
    },
  };
}

export function createPlatformStorage(kind: RuntimeKind): PlatformStorage {
  const backend = RUNTIME_CAPABILITIES[kind].secureStorage;
  if (backend === 'electron-safe-storage') return createElectronSecureStorage();
  return createDelegatingStorage(backend);
}
